import * as React from 'react';

import {Text} from './Text';
import {InitialAvatar} from './InitialAvatar';
import {Tag} from './Tag';
import {calculateAge, isMinor} from '../../module/dates/dates.utils';

function avatarColorFor(dateOfBirth?: string): string | undefined {
  if (!dateOfBirth || calculateAge(dateOfBirth) === undefined) {
    return undefined;
  }
  return isMinor(dateOfBirth) ? 'bg-sandwisp' : 'bg-periods';
}

function ageLabel(dateOfBirth?: string): string | null {
  const age = dateOfBirth ? calculateAge(dateOfBirth) : undefined;
  return age === undefined ? null : `${age} years old`;
}

type Props = {
  name: string;
  dateOfBirth?: string;
  tag?: {title: string; color: string};
  muted?: boolean;
  action?: React.ReactNode;
  ageSuffix?: React.ReactNode;
};

// A single connected-teen/member row — avatar (coloured by minor/non-minor),
// name, computed age, and an optional status pill or action. Shared by
// ConnectedTeens (parent home) and TeenInfoForm (get-started), so both
// surfaces show a teen the exact same way.
export const PersonCard = ({
  name,
  dateOfBirth,
  tag,
  muted,
  action,
  ageSuffix,
}: Props) => {
  const age = ageLabel(dateOfBirth);

  return (
    <div
      className={`flex items-center gap-3 rounded-2xl border border-divider bg-white p-4 sm:p-6 ${muted ? 'opacity-60' : ''}`}>
      <InitialAvatar
        label={name}
        colorClassName={avatarColorFor(dateOfBirth)}
      />
      <div className="flex-1">
        <Text as="p" variant="SmallBodyBold">
          {name}
        </Text>
        {(age || ageSuffix) && (
          <div className="flex flex-col gap-1.5 text-weak sm:flex-row sm:items-center sm:gap-1.5">
            {age && (
              <Text as="span" variant="Caption" className="text-weak">
                {age}
              </Text>
            )}
            {age && ageSuffix && (
              <span aria-hidden="true" className="hidden sm:inline">
                |
              </span>
            )}
            {ageSuffix && (
              <span className="hidden sm:inline-flex sm:items-center">
                {ageSuffix}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col items-center gap-2">
        {ageSuffix && <span className="sm:hidden">{ageSuffix}</span>}
        {action ?? (tag && <Tag color={tag.color} title={tag.title} />)}
      </div>
    </div>
  );
};
